import { PricedVariant } from '@medusajs/medusa/dist/types/pricing';

import {
  ConvertibleNode,
  HeadingNode,
  ListItem,
  ListNode,
  ParagraphNode,
  TextNode,
} from './strapi/types/api';

const displayFormattedCurrency = (amount: number, currencyCode: string) => {
  return (amount / 100)
    .toLocaleString('en-EN', {
      style: 'currency',
      minimumFractionDigits: 0,
      currency: currencyCode,
      useGrouping: true,
      currencyDisplay: 'symbol',
    })
    .replace(',', ' ');
};

export const formatPrice = (amount: number, currency_code = 'PLN') => {
  return displayFormattedCurrency(amount, currency_code);
};

export const singleVariantPrice = (
  variant: PricedVariant,
  currency_code = 'PLN',
) => {
  if (variant.prices.length !== 0) {
    const { amount } = variant.prices[0];
    return displayFormattedCurrency(amount, currency_code);
  }

  return '';
};

export const getPrice = (variants: PricedVariant[]) => {
  if (variants.length === 1) {
    if (variants[0].prices.length !== 0) {
      const { amount, currency_code } = variants[0].prices[0];

      return displayFormattedCurrency(amount, currency_code);
    }
  } else {
    const prices = variants.map(variant => variant.prices).flat();
    const lowestPrice = prices.reduce((prev, curr) =>
      prev.amount < curr.amount ? prev : curr,
    );
    const { amount, currency_code } = lowestPrice;

    return displayFormattedCurrency(amount, currency_code);
  }
};

export const convertToHtml = (data: ConvertibleNode[]): string => {
  let htmlOutput = '';

  const formatText = (child: TextNode): string => {
    let text = child.text;

    if (child.type === 'link') {
      return `<a href=${child.url}>${
        child.children && formatText(child?.children[0])
      }</a>`;
    }
    if (child.text === '') {
      return `<br />`;
    }

    if (child.bold) {
      text = `<strong>${text}</strong>`;
    }
    if (child.italic) {
      text = `<em>${text}</em>`;
    }
    if (child.underline) {
      text = `<u>${text}</u>`;
    }
    return text;
  };

  const processParagraph = (item: ParagraphNode): string => {
    const paragraphContent = item.children.map(formatText).join('');
    return `<p>${paragraphContent}</p>`;
  };

  const processHeading = (item: HeadingNode): string => {
    const headingContent = item.children.map(formatText).join('');
    return `<h${item.level}>${headingContent}</h${item.level}>`;
  };

  const processList = (item: ListNode): string => {
    const listTag = item.format === 'unordered' ? 'ul' : 'ol';
    const listItems = item.children
      .map((listItem: ListItem) => {
        const listItemContent = listItem.children.map(formatText).join('');
        return `<li>${listItemContent}</li>`;
      })
      .join('');
    return `<${listTag}>${listItems}</${listTag}>`;
  };

  data.forEach((item: ConvertibleNode) => {
    switch (item.type) {
      case 'paragraph':
        htmlOutput += processParagraph(item);
        break;

      case 'heading':
        htmlOutput += processHeading(item);
        break;

      case 'list':
        htmlOutput += processList(item);
        break;

      default:
        //@ts-ignore
        console.warn(`Unknown item type: ${item.type}`);
        break;
    }
  });

  return htmlOutput;
};

export const convertFileSize = (size: number) => {
  const convertedSize = `${(size / 100).toFixed(2)}  MB`;

  return convertedSize;
};
